import React, { useEffect, useState } from "react";
import { Tile, Badge } from "../components/Primitives";
import { formatNumber, cn } from "../lib/utils";
import { ComposedChart, Line, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Sparkles } from "lucide-react";
import { useChartColors } from "../lib/chartColors";
import ForecastLayout, { useDrill } from "../components/forecast/ForecastLayout";
import { KpiStrip, DriverPanel, LineButton, PageState, Cue } from "../components/forecast/Lines";
import { usePage, getSeries, testid, whenText } from "../lib/forecastApi";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.filter(p => p.value !== null && p.name !== "ciUpper" && p.name !== "ciLower").map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color }} /><span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, status }) {
  const c = { success: "text-success", warning: "text-warning", critical: "text-critical" }[status];
  return <div><div className="kpi-label">{label}</div><div className={cn("kpi-value text-lg mt-1", c)}>{value}</div></div>;
}

function SkuPicker({ skus, selected, onPick, origin }) {
  return (
    <Tile className="col-span-12 lg:col-span-3" testid="sku-picker" origin={{ origin, source: "Demand forecast engine · SKU × branch lines" }} title="Pick a SKU" dense>
      <div className="max-h-[440px] overflow-y-auto divide-y divide-border">
        {skus.map(s => (
          <button key={s.sku_id} data-testid={`sku-${testid(s.sku_id)}`} onClick={() => onPick(s.sku_id)}
                  className={cn("w-full text-left px-4 py-2.5 hover:bg-surface-2 transition-colors",
                    selected === s.sku_id ? "bg-primary/10 border-l-2 border-primary -ml-[2px] pl-[14px]" : "")}>
            <div className="text-sm font-medium truncate">{s.sku_name}</div>
            <div className="text-[10px] text-muted font-mono-tabular">{s.sku_id} · {s.category} · {s.class} · {formatNumber(s.forecast_30d)} u / 30 d</div>
          </button>
        ))}
      </div>
    </Tile>
  );
}

function ForecastChart({ sku }) {
  const C = useChartColors();
  const { open } = useDrill();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); getSeries(sku).then(setD).catch(() => setD(false)); }, [sku]);
  if (!d) return <Tile className="col-span-12 lg:col-span-9" testid="forecast-chart"><div className="h-[400px] text-sm text-muted p-4">{d === false ? "Couldn't load this forecast." : "Loading forecast…"}</div></Tile>;
  const st = d.stats;
  const today = d.series.find(p => p.forecast !== null)?.day;
  return (
    <Tile className="col-span-12 lg:col-span-9" testid="forecast-chart" origin={{ origin: d.origin, source: "Demand forecast engine · daily demand history (sold + lost)" }}
          title={<span className="font-display text-base font-semibold truncate">{d.sku_name}</span>}
          action={<div className="flex items-center gap-2 flex-wrap justify-end">
            <Badge status="info"><Sparkles size={11} /> {d.models.join(" · ")}</Badge>
            {st.growth_pct !== null && <Badge status={st.growth_pct > 0 ? "success" : "warning"}>{st.growth_pct > 0 ? "+" : ""}{st.growth_pct}% next 30d</Badge>}
            <button data-testid="forecast-chart-explain" onClick={() => open(d.line)} className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"><Sparkles size={11} />Explain</button>
          </div>}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Stat label="Forecast (next 30d)" value={`${formatNumber(st.forecast_30d)} u`} />
        <Stat label="Prior 30d (actual)" value={`${formatNumber(st.prior_30d)} u`} />
        <Stat label="80% band (daily)" value={st.band_pct !== null ? `±${st.band_pct}%` : "—"} />
        <Stat label="Stockout risk (30d)" value={st.stockout_risk} status={{ Low: "success", Medium: "warning", High: "critical" }[st.stockout_risk]} />
      </div>
      <div style={{ height: 320 }}>
        <ResponsiveContainer>
          <ComposedChart data={d.series} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="fc-ci" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.chart[1]} stopOpacity="0.32" /><stop offset="100%" stopColor={C.chart[1]} stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
            <XAxis dataKey="day" tick={tickStyle} stroke={C.borderStrong} interval={9} />
            <YAxis tick={tickStyle} stroke={C.borderStrong} />
            <Tooltip content={<TT />} cursor={{ stroke: C.borderStrong }} />
            <ReferenceLine x={today} stroke={C.primary} strokeDasharray="4 4" label={{ value: "Today", fill: C.primary, fontSize: 10, position: "insideTopRight" }} />
            <Area dataKey="ciUpper" name="ciUpper" stroke="none" fill="url(#fc-ci)" isAnimationActive={false} />
            <Area dataKey="ciLower" name="ciLower" stroke="none" fill={C.surface} isAnimationActive={false} />
            <Line dataKey="actual" name="Actual" stroke={C.chart[0]} strokeWidth={2.2} dot={false} isAnimationActive={false} />
            <Line dataKey="forecast" name="Forecast" stroke={C.chart[1]} strokeWidth={2.2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Tile>
  );
}

function ModelPerformance({ rows, lastRun, origin }) {
  return (
    <Tile className="col-span-12 lg:col-span-6" title="Model Performance — 14-day backtest" testid="model-perf" dense
          origin={{ origin, source: "Demand forecast engine · 14-day holdout backtest" }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted">
              {["Model", "Lines", "WAPE", "Naive WAPE", "Last trained", "Status"].map((h, i) => (
                <th key={h} className={cn("py-2 px-3 border-b border-border", i > 0 && i < 5 ? "text-right" : "text-left")}>{h}</th>))}
            </tr>
          </thead>
          <tbody className="font-mono-tabular">
            {rows.map(m => (
              <LineButton key={m.model} as="tr" line={{ id: m.line }} className="border-b border-border last:border-b-0 table-row">
                <td className="py-2 px-3 font-sans">{m.model} <Cue /></td>
                <td className="py-2 px-3 text-right">{m.lines}</td>
                <td className={cn("py-2 px-3 text-right", m.wape < 0.3 ? "text-success" : m.wape < 0.45 ? "text-warning" : "text-critical")}>{(m.wape * 100).toFixed(1)}%</td>
                <td className="py-2 px-3 text-right text-muted">{(m.naive_wape * 100).toFixed(1)}%</td>
                <td className="py-2 px-3 text-right text-[11px]">{whenText(lastRun)}</td>
                <td className="py-2 px-3 font-sans"><Badge status={m.wape < m.naive_wape ? "success" : "info"}>{m.wape < m.naive_wape ? "Beats baseline" : "Baseline"}</Badge></td>
              </LineButton>
            ))}
          </tbody>
        </table>
      </div>
    </Tile>
  );
}

// Demand Forecast — the original layout, fed by the platform's demand-forecast engine.
export default function Forecast() {
  const { data, error, forbidden, reload } = usePage("demand");
  const [sku, setSku] = useState(null);
  useEffect(() => { if (data && !sku) setSku(data.skus[0]?.sku_id); }, [data, sku]);
  return (
    <ForecastLayout eyebrow="Forecast / Prediction Models · Model 01" title="Demand Forecast"
      description="SKU × branch forecasts from the demand-forecast engine: model routed per line by backtest, adjusted for heat (NASA POWER) and install base.">
      {!data ? <PageState error={error} forbidden={forbidden} onRetry={reload} /> : (
        <>
          {data.meta.uploaded_stage && <div data-testid="fc-uploaded-stage"><Badge status="success">Actual · {data.meta.uploaded_stage === "gold" ? "Gold — certified" : "Silver — in training"} · uploaded data</Badge></div>}
          <KpiStrip kpis={data.kpis} cols="md:grid-cols-5" />
          <div className="grid grid-cols-12 gap-6">
            <SkuPicker skus={data.skus} selected={sku} onPick={setSku} origin={data.kpis[0].origin} />
            {sku && <ForecastChart sku={sku} />}
            <div className="col-span-12 lg:col-span-6" data-testid="forecast-drivers">
              <DriverPanel title="Forecast Drivers" hint="Estimated effect on the forecast · click for the explanation" drivers={data.drivers} centered />
            </div>
            <ModelPerformance rows={data.performance} lastRun={data.meta.engine_run_at} origin={data.kpis[0].origin} />
          </div>
        </>
      )}
    </ForecastLayout>
  );
}

